<template>
    <x-container id="page-content" fluid app-width>
        <x-layout>
            <x-flex>
                <template v-if="loading">
                    <loading-spinner />
                </template>

                <x-container v-else fluid class="summary-container">
                    <x-layout>
                        <x-flex xs12>
                            <summary-tabs-buttons
                                v-model="activeTab"
                                :tabs="summaryTabs"
                            />
                        </x-flex>
                    </x-layout>

                    <view-details-banner
                        v-if="Boolean(this.pendingApprovedEndorsement)"
                        :text="pendingEndorsementText"
                        :scroll-to-id="pendingApprovedEndorsement.type"
                        class="mb-3"
                        @clicked="showProductTab()"
                    />

                    <view-details-banner
                        v-if="Boolean(this.pendingApprovedEndorsement) && isStandalonePolicyCheck(declaration)"
                        text="Review the draft Market Reform Contract and Policy Wording."
                        scroll-to-id="page-content"
                        class="mb-3"
                        @clicked="showDocumentsTab()"
                    />

                    <template v-if="activeTab === 'product'">
                        <x-layout>
                            <x-flex xs6 class="left">
                                <x-card class="panel mb-5">
                                    <x-card-title>
                                        <h3>{{ declarationDetailsHeading() }}</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <standalone-policy-summary-data-list v-if="isStandalonePolicyCheck(declaration)" v-model="declaration" />

                                        <summary-data-list v-else v-model="declaration" />
                                    </x-card-text>
                                </x-card>

                                <x-card v-if="isCaptiveReinsurance" class="panel mb-5">
                                    <x-card-title>
                                        <h3>Reinsurer declaration details</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <summary-data-list v-model="declaration" :is-reinsurer="true" />
                                    </x-card-text>
                                </x-card>
                            </x-flex>

                            <x-flex xs6 class="right">
                                <x-card class="panel mb-5">
                                    <x-card-title>
                                        <h3>Signed lines</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <signed-lines-table
                                            table-class="inverse"
                                            :lines="declaration.lines"
                                            :facility="declaration.facility"
                                            :currency-code="declaration.currency.code"
                                            :show-final-premium="hasFinalPremium"
                                            :show-edit="true"
                                            :isStandalonePolicy="isStandalonePolicyCheck(declaration)"
                                        />
                                    </x-card-text>
                                </x-card>

                                <x-card v-if="hasObligor" class="panel mb-5">
                                    <x-card-title>
                                        <h3>Obligors</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <obligor-data-list v-model="declaration.obligors" obligor :currency-code="declaration.currency.code" />
                                    </x-card-text>
                                </x-card>

                                <x-card v-if="hasObligor && declaration.guarantors.length > 0" class="panel mb-5">
                                    <x-card-title>
                                        <h3>Guarantors</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <obligor-data-list v-model="declaration.guarantors" />
                                    </x-card-text>
                                </x-card>
                            </x-flex>
                        </x-layout>

                        <x-layout id="repayment" mb-5>
                            <x-flex>
                                <h3 class="pb-3">Repayments</h3>

                                <repayments-table
                                    :repayments="repayments"
                                    :declaration="declaration"
                                />
                            </x-flex>
                        </x-layout>

                        <x-layout id="mid-term-adjustment" mb-5>
                            <x-flex>
                                <h3 class="pb-3">Mid-term adjustment</h3>

                                <mid-term-adjustment-table
                                    :mid-term-adjustment="midTermAdjustments"
                                    :declaration="declaration"
                                    :mta-loading="mtaLoading"
                                    userType="insurer"
                                />
                            </x-flex>
                        </x-layout>

                        <x-layout id="amendment" mb-5>
                            <x-flex>
                                <h3 class="pb-3">Amendments</h3>

                                <amendments-table :declaration="declaration" :is-captive-declaration="isCaptiveReinsurance" />
                            </x-flex>
                        </x-layout>

                        <x-layout v-if="isCommitment" mb-5>
                            <x-flex>
                                <h3 class="pb-3">Invoices</h3>

                                <invoices-table :invoices="declaration.invoices" />
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex>
                                <h3 class="pb-3">Version history</h3>

                                <version-history-table
                                    :risk="declaration"
                                    :audits="versionHistoryRows"
                                    :product-config-key="declaration.product_config_key"
                                    userType="insurer"
                                    :currency-code="declaration.currency.code"
                                />
                            </x-flex>
                        </x-layout>
                    </template>

                    <template v-if="activeTab === 'additional_information'">
                        <x-layout>
                            <x-flex>
                                <additional-information-tab
                                    v-model="declaration.information_sections"
                                    insurer
                                />
                            </x-flex>
                        </x-layout>
                    </template>

                    <template v-if="activeTab === 'documents'">
                        <document-list
                            id="documents"
                            :documentable-id="declaration.id"
                            documentable-type="declaration"
                            :can-delete="() => false"
                        />
                    </template>
                </x-container>
            </x-flex>
        </x-layout>

        <modal
            v-if="Boolean(endorsementWaitingApproval)"
            ref="approveEndorsement"
            :title="endorsementApprovalText"
            confirmation
        >
            <p>
                <span v-if="endorsementWaitingApproval && endorsementWaitingApproval.type === 'mid_term_adjustment'">You are about to approve the mid-term adjustment.</span>

                <span v-else>You are about to approve the endorsement.</span>
            </p>

            <p>Are you sure?</p>
        </modal>
    </x-container>
</template>


<script>
    import SummaryTabsButtons from '~/components/Shared/UIComponents/SummaryTabsButtons';
    import StatusChip from "~/components/Shared/UIComponents/StatusChip";
    import OffRiskApproval from "~/components/Insurer/OffRiskApproval";
    import AmendmentsTable from "~/components/Insurer/AmendmentsTable";
    import InvoicesTable from '~/components/Shared/UIComponents/InvoicesTable';
    import DocumentList from '~/components/Shared/UIComponents/DocumentList.vue';
    import RepaymentsTable from "~/components/Shared/UIComponents/RepaymentsTable";
    import MidTermAdjustmentTable from '~/components/Shared/UIComponents/MidTermAdjustmentTable';
    import VersionHistoryTable from '~/components/Shared/UIComponents/VersionHistoryTable';
    import SummaryDataList from '~/components/Shared/UIComponents/SummaryDataList';
    import SignedLinesTable from '~/components/Broker/UIComponents/SignedLinesTable';
    import AdditionalInformationTab from '~/components/Shared/UIComponents/AdditionalInformationTab';
    import ObligorDataList from "~/components/Shared/UIComponents/ObligorDataList";
    import Modal from '~/components/Shared/UIComponents/Modal';
    import ViewDetailsBanner from "../../../components/Shared/UIComponents/ViewDetailsBanner.vue";
    import submissionHelpers from "../../../mixins/submissionHelpers";
    import StandalonePolicySummaryDataList from '../../../components/Shared/UIComponents/StandalonePolicySummaryDataList.vue';

    export default {
        name: "InsurerDeclarationSummary",
        components: {
            SummaryTabsButtons,
            ViewDetailsBanner,
            AmendmentsTable,
            InvoicesTable,
            StatusChip,
            DocumentList,
            OffRiskApproval,
            RepaymentsTable,
            MidTermAdjustmentTable,
            VersionHistoryTable,
            SummaryDataList,
            SignedLinesTable,
            AdditionalInformationTab,
            ObligorDataList,
            Modal,
            StandalonePolicySummaryDataList
        },
        mixins: [submissionHelpers],
        data() {
            return {
                activeTab: 'product',
                declaration: {
                    endorsement_request: null
                },
                openDeclarationDetailsPanelIndex: 0,
                openSignedLinesPanelIndex: 0,
                profile: {},
                loading: true,
                repayments: [],
                midTermAdjustments: [],
                mtaLoading: false,
                declarationAudit: [],
                openAmendmentPanelIndex: null,
                openRepaymentPanelIndex: null,
                openDocumentsPanelIndex: 1
            }
        },
        created() {
            this.fetchData();

            this.$eventBus.$on('declarationChanged', async () => {
                await this.fetchData();
            });

            this.$eventBus.$on('action-download-mrc', () => this.download('mrc'));
            this.$eventBus.$on('action-download-summary', () => this.download('declaration'));
            this.$eventBus.$on('action-download-policy-wording', () => this.download('policy-wording'));
            this.$eventBus.$on('action-download-captive-summary', () => this.download('captive-declaration'));
            this.$eventBus.$on('action-download-reinsurer-summary', () => this.download('reinsurer-declaration'));
            this.$eventBus.$on('action-approve-endorsement', () => this.approveEndorsement());
        },
        beforeDestroy() {
            this.$eventBus.$off('action-download-mrc');
            this.$eventBus.$off('action-download-summary');
            this.$eventBus.$off('action-download-policy-wording');
            this.$eventBus.$off('action-download-captive-summary');
            this.$eventBus.$off('action-download-reinsurer-summary');
            this.$eventBus.$off('action-approve-endorsement');
        },
        methods: {
            declarationDetailsHeading() {
                if (this.isStandalonePolicyCheck(this.declaration)) {
                    return 'Policy details';
                }
                return this.isCaptiveReinsurance ? 'Original declaration details' : 'Declaration details';
            },
            async fetchData() {
                this.loading = true;
                this.mtaLoading = true;
                const dataRequests = [];

                dataRequests.push(axios.get(this.$root.api(`insurer/declaration/${this.$route.params.id}`)).then(res => {
                    this.declaration = res.data.data;
                }));

                await axios.all(dataRequests).then(() => {
                    axios.get(this.$root.api(`insurer/repayments/${this.declaration.id}`)).then(res => {
                        this.repayments = res.data.data;
                    });

                    axios.get(this.$root.api(`mid-term-adjustment/${this.declaration.id}`)).then(res => {
                        this.midTermAdjustments = res.data.data;
                        this.mtaLoading = false;
                    });

                    axios.get(this.$root.api(`audit/declarations/${this.declaration.id}`)).then(res => {
                        this.declarationAudit = res.data.data;
                    });

                    this.loading = false;
                });
            },
            async download(type) {
                let url = null;
                switch (type) {
                    case 'mrc':
                        url = `/api/declaration/${this.$route.params.id}/documents/mrc-download/${this.declaration.id}`;
                        break;
                    case 'policy-wording':
                        url = `/api/declaration/${this.$route.params.id}/documents/policy-wording-download/${this.declaration.id}`;
                        break;
                    case 'declaration':
                        url = `/api/declaration/${this.$route.params.id}/documents/download/${this.declaration.id}`;
                        break;
                    case 'captive-declaration':
                        url = `/api/declaration/${this.$route.params.id}/documents/captive-download/${this.declaration.id}`;
                        break;
                    case 'reinsurer-declaration':
                        url = `/api/declaration/${this.$route.params.id}/documents/reinsurer-download/${this.declaration.id}`;
                        break;
                }

                if (url !== null || typeof url !== "undefined") {
                    window.location.href = (await axios.get(url)).data.url;
                }
                return false;
            },
            async approveEndorsement() {
                if (await this.$refs.approveEndorsement.open() === false) {
                    return;
                }
                this.loading = true;
                await axios.put(this.$root.api(`insurer/endorsement/${this.endorsementWaitingApproval.id}/captive-approve`));
                this.loading = false;
                this.$router.push('/insurer/dashboard');
            },
            showProductTab() {
                this.activeTab = 'product';
            },
            showDocumentsTab() {
                this.activeTab = 'documents';
            },
            isDeclarationHasPendingApprovedRequest(endorsement) {
                return ['pending', 'approved'].includes(endorsement.status);
            },
            hasPendingEndorsementRequest() {
                return this.declaration.endorsement_request !== null &&
                        this.declaration.endorsement_request.endorsement.status === 'pending' &&
                        this.declaration.endorsement_request.status === 'pending';
            },
        },
        computed: {
            summaryTabs() {
                const tabs = [
                    {
                        value: 'product',
                        text: 'Product',
                    },
                ];

                if (this.declaration.information_sections.length > 0) {
                    tabs.push({
                        value: 'additional_information',
                        text: 'Additional Information',
                    })
                }

                tabs.push(
                    {
                        value: 'documents',
                        text: 'Documents',
                    },
                );

                return tabs;
            },
            pendingEndorsementText() {
                return `An ${this.pendingApprovedEndorsement.type} is ${this.pendingApprovedEndorsement.status === 'captive_approved' ? 'pending' : this.pendingApprovedEndorsement.status} and will be applied on
                ${this.$options.filters.formatDateEnglishShort(this.pendingApprovedEndorsement.effective_date)}.`;
            },
            actionMenuItems() {
                const actionItems = [
                    {
                        name: `Download ${this.isStandalonePolicyCheck(this.declaration) ? 'policy' : 'declaration'} summary`,
                        eventName: 'action-download-summary'
                    }
                ];
                if (this.declaration.is_mrc_downloadable && (this.declaration.status === 'bound' || this.declaration.status === 'expired')) {
                    actionItems.push({
                        name: 'Download MRC',
                        eventName: 'action-download-mrc'
                    });
                }

                if (this.isStandalonePolicyCheck(this.declaration)) {
                    actionItems.push({
                        name: `Download policy wording`,
                        eventName: 'action-download-policy-wording'
                    });
                }

                if (this.isCaptiveReinsurance) {
                    actionItems.push({
                        name: 'Download original declaration summary',
                        eventName: 'action-download-captive-summary'
                    });
                    actionItems.push({
                        name: 'Download reinsurer declaration summary',
                        eventName: 'action-download-reinsurer-summary'
                    });
                }

                if (this.hasAmendmentPendingEndorsement) {
                    actionItems.push({
                        name: 'Review amendment',
                        eventName: 'action-review-amendment'
                    })
                }

                if (this.hasPendingMidTermAdjustment) {
                    actionItems.push({
                        name: 'Review Mid-term Adjustment',
                        eventName: 'action-review-mid-term-adjustment'
                    })
                }

                if (this.hasOffRiskPendingEndorsement) {
                    actionItems.push({
                        name: 'Review final premium',
                        eventName: 'action-review-off-risk'
                    })
                }

                if (this.endorsementWaitingApproval) {
                    actionItems.push({
                        name: this.endorsementApprovalText,
                        eventName: 'action-approve-endorsement'
                    })
                }

                return actionItems;
            },
            isCaptiveReinsurance() {
                return this.declaration.transaction_type === 'captive_reinsurance';
            },
            hasObligor() {
                return this.declaration.hasOwnProperty('product') && !this.declaration.product.has_no_obligor;
            },
            endorsements() {
                return this.declaration.endorsements;
            },
            pendingApprovedEndorsement: function () {
                const self = this;
                return this.declaration.endorsements.find(function (endorsement) {
                    return self.isDeclarationHasPendingApprovedRequest(endorsement) &&
                            (endorsement.type === 'repayment' || endorsement.type === 'amendment');
                });
            },
            endorsementWaitingApproval: function () {
                if (!this.$root.userIsCaptiveUser() || !this.isCaptiveReinsurance) {
                    return false;
                }
                return this.waitingApprovalEndorsement;
            },
            waitingApprovalEndorsement() {
                return this.declaration.endorsements.find(function (endorsement) {
                    return (endorsement.status === 'captive_approved');
                });
            },
            endorsementApprovalText() {
                if (this.waitingApprovalEndorsement && this.waitingApprovalEndorsement.type === 'mid_term_adjustment') {
                    return 'Approve MTA';
                }

                return 'Approve endorsement';
            },
            hasAmendmentPendingEndorsement() {
                return  this.hasPendingEndorsementRequest() && this.declaration.endorsement_request.endorsement.type === 'amendment';
            },
            hasOffRiskPendingEndorsement() {
                return this.hasPendingEndorsementRequest() && this.declaration.endorsement_request.endorsement.type === 'off_risk';
            },
            hasPendingMidTermAdjustment() {
                return this.hasPendingEndorsementRequest() && this.declaration.endorsement_request.endorsement.type === 'mid_term_adjustment';
            },
            myLines() {
                return this.declaration.lines.filter(line => line.insurer_company_id === this.$root.user.company_id);
            },
            myOfferedLine() {
                return this.myLines[0] || {};
            },
            versionHistoryRows() {
                return this.declarationAudit.filter(audit => ((audit.origin === 'Endorsement') || (audit.event === 'created' && audit.auditable_type === 'App\\Broker\\Models\\Declaration')));
            },
            hasFinalPremium() {
                return this.myLines.some(line => Boolean(line.final_premium));
            },
            showOffRiskPendingLabel() {
                if (!this.declaration.endorsements) {
                    return false;
                }

                return this.declaration.endorsements.find(e => e.type === 'off_risk' && this.isDeclarationHasPendingApprovedRequest(e));
            },
            isCommitment() {
                return this.declaration.quote_type.key === 'commitment';
            },
        },
        watch: {
            actionMenuItems: {
                handler(newItems) {
                    this.$eventBus.$emit('update-action-menu-items', newItems);
                },
                immediate: true
            },
            showOffRiskPendingLabel: {
                handler(hasActiveOffRisk) {
                    this.$eventBus.$emit('update-has-active-off-risk', Boolean(hasActiveOffRisk));
                },
                immediate: true
            }
        }
    }
</script>

<style lang="scss" scoped>
h3 {
    font-size: rem(24px);
}
</style>
